import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api } from "@/lib/soul";
import { useAuth } from "@/context/AuthContext";
import Paywall from "@/components/Paywall";
import { toast } from "sonner";
import { RefreshCw, Trash2, Feather, History, X, Sparkles, Wand2 } from "lucide-react";

const DAY = 86400000;
const THEN_NOW_MIN_DAYS = 80; // resurface entries from roughly three months back

function daysAgo(iso) {
  return Math.floor((Date.now() - new Date(iso).getTime()) / DAY);
}

function relativeTime(iso) {
  const d = daysAgo(iso);
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 7) return `${d} days ago`;
  if (d < 30) return `${Math.floor(d / 7)} week${Math.floor(d / 7) === 1 ? "" : "s"} ago`;
  if (d < 365) return `${Math.floor(d / 30)} month${Math.floor(d / 30) === 1 ? "" : "s"} ago`;
  return `${Math.floor(d / 365)} year${Math.floor(d / 365) === 1 ? "" : "s"} ago`;
}

function longDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

export default function Journal() {
  const { user } = useAuth();
  const isPaid = user && (user.tier === "seeker" || user.tier === "contributor");
  const [prompts, setPrompts] = useState([]);
  const [moods, setMoods] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [mood, setMood] = useState("");
  const [content, setContent] = useState("");
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dismissedThenNow, setDismissedThenNow] = useState(false);
  const [living, setLiving] = useState(null);
  const [livingMode, setLivingMode] = useState("short");
  const [livingLoading, setLivingLoading] = useState(false);
  const textareaRef = useRef(null);

  const loadEntries = useCallback(() => {
    api.get(`/journal`).then((r) => setEntries(r.data)).catch(() => {});
  }, []);

  const loadLiving = useCallback((mode) => {
    setLivingLoading(true);
    api
      .get(`/journal/living-prompt?mode=${mode}`)
      .then((r) => setLiving(r.data.result))
      .catch(() => {})
      .finally(() => setLivingLoading(false));
  }, []);

  useEffect(() => {
    api.get("/prompts").then((r) => {
      setPrompts(r.data.prompts);
      setMoods(r.data.moods);
      setPrompt(r.data.prompts[Math.floor(Math.random() * r.data.prompts.length)]);
    });
    loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    loadLiving(livingMode);
  }, [livingMode, loadLiving]);

  // Most recent entry that is at least ~3 months old — the "Then vs. Now" candidate.
  const thenNow = useMemo(
    () => entries.find((e) => daysAgo(e.created_at) >= THEN_NOW_MIN_DAYS) || null,
    [entries]
  );

  const shuffle = () => setPrompt(prompts[Math.floor(Math.random() * prompts.length)]);

  const reflectOnPast = () => {
    setPrompt(`Then vs. Now — revisiting my reflection from ${longDate(thenNow.created_at)}. How do I see this now?`);
    textareaRef.current?.focus();
    textareaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const save = async () => {
    if (!content.trim()) {
      toast.error("Write your truth before saving.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/journal", { prompt, content, mood });
      setContent("");
      setMood("");
      toast.success("Your reflection has been kept.");
      loadEntries();
    } catch {
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/journal/${id}`);
      toast.success("Released.");
      loadEntries();
    } catch {
      toast.error("Could not delete.");
    }
  };

  const welcome =
    entries.length > 0
      ? `Welcome back — your last reflection was ${relativeTime(entries[0].created_at)}. Pick the thread back up below.`
      : "A quiet, unhurried space that is entirely yours. What we hesitate to say aloud is often the very thing that sets us free.";

  return (
    <main data-testid="journal-page" className="max-w-3xl mx-auto px-6 py-16">
      <div className="mb-12 fade-up">
        <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4">Daily Truth Journal</p>
        <h1 className="serif text-4xl sm:text-6xl font-light text-[#F5F1E7]">
          Write the truth you <span className="italic-serif text-[#D4AF37]">hesitate to own</span>
        </h1>
        <p data-testid="journal-welcome" className="mt-5 text-[#A8A29E] leading-relaxed text-lg">{welcome}</p>
      </div>

      {!isPaid ? (
        <Paywall
          testId="journal-paywall"
          title="The Journal is part of the Seeker path"
          message="Your private Truth Journal — with Living Prompts, Mood Pulse and gentle 'Then vs. Now' reflections — opens with Seeker. Step in when you're ready."
          tier="Seeker"
        />
      ) : (
      <>
      {/* Living prompt — a caring, AI-guided greeting drawn from recent entries */}
      <div data-testid="living-prompt" className="mb-8 glass rounded-2xl p-6 border border-[#D4AF37]/20">
        <div className="flex items-center justify-between gap-4 mb-3">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
            <Sparkles size={14} /> A word for you today
          </p>
          <div className="flex items-center gap-1 rounded-full border border-white/10 p-1">
            <button
              data-testid="living-mode-short"
              onClick={() => setLivingMode("short")}
              className={`text-xs px-3 py-1 rounded-full transition-colors duration-300 ${livingMode === "short" ? "bg-[#D4AF37] text-[#17100C]" : "text-[#A8A29E] hover:text-[#F5F1E7]"}`}
            >
              Quick check-in
            </button>
            <button
              data-testid="living-mode-deep"
              onClick={() => setLivingMode("deep")}
              className={`text-xs px-3 py-1 rounded-full transition-colors duration-300 ${livingMode === "deep" ? "bg-[#D4AF37] text-[#17100C]" : "text-[#A8A29E] hover:text-[#F5F1E7]"}`}
            >
              Deep dive
            </button>
          </div>
        </div>
        {livingLoading ? (
          <p className="text-[#A8A29E] italic-serif text-lg">Listening for what you've been carrying…</p>
        ) : living ? (
          <>
            {living.greeting && (
              <p data-testid="living-greeting" className="text-[#F5F1E7] italic-serif text-xl leading-relaxed">{living.greeting}</p>
            )}
            <p data-testid="living-prompt-text" className="mt-3 text-[#D4AF37] text-lg leading-relaxed">{living.prompt}</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                data-testid="living-use-btn"
                onClick={() => { setPrompt(living.prompt); textareaRef.current?.focus(); }}
                className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors duration-300"
              >
                <Wand2 size={15} /> Write with this
              </button>
              <button
                data-testid="living-refresh-btn"
                onClick={() => loadLiving(livingMode)}
                className="inline-flex items-center gap-2 text-sm text-[#A8A29E] hover:text-[#D4AF37] transition-colors duration-300"
              >
                <RefreshCw size={14} /> Another
              </button>
            </div>
          </>
        ) : null}
      </div>

      {/* Composer */}
      <div className="space-y-6">
        <div className="glass rounded-2xl p-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#A8A29E] mb-2">Prompt</p>
            <p data-testid="journal-prompt" className="italic-serif text-2xl text-[#F5F1E7] leading-snug">
              {prompt}
            </p>
          </div>
          <button
            data-testid="shuffle-prompt-btn"
            onClick={shuffle}
            className="shrink-0 text-[#D4AF37] hover:rotate-180 transition-transform duration-500"
            title="New prompt"
          >
            <RefreshCw size={20} />
          </button>
        </div>

        <div className="glass rounded-2xl p-6 gold-glow">
          <textarea
            ref={textareaRef}
            data-testid="journal-textarea"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Let it be honest..."
            rows={8}
            className="w-full bg-transparent resize-none outline-none text-[#F5F1E7] text-lg leading-relaxed placeholder:text-[#A8A29E]/50"
          />
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-white/5 pt-4">
            <span className="text-xs uppercase tracking-[0.2em] text-[#A8A29E] mr-2">Mood</span>
            {moods.map((m) => (
              <button
                key={m}
                data-testid={`mood-${m}`}
                onClick={() => setMood(m === mood ? "" : m)}
                className={`text-sm px-3 py-1 rounded-full border transition-colors duration-300 ${
                  mood === m
                    ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                    : "border-white/10 text-[#A8A29E] hover:border-[#D4AF37]/40"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          <button
            data-testid="save-journal-btn"
            onClick={save}
            disabled={saving}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#110D14] px-7 py-3 font-medium hover:bg-[#e6c860] transition-colors duration-300 disabled:opacity-50"
          >
            <Feather size={18} />
            {saving ? "Keeping..." : "Keep this reflection"}
          </button>
        </div>
      </div>

      {/* Then vs. Now — resurfaced entry */}
      {thenNow && !dismissedThenNow && (
        <div
          data-testid="then-vs-now"
          className="mt-12 glass rounded-2xl p-6 border border-[#D4AF37]/30 relative"
          style={{ background: "radial-gradient(600px 200px at 100% 0%, rgba(212,175,55,0.08), transparent 70%)" }}
        >
          <button
            data-testid="then-vs-now-dismiss"
            onClick={() => setDismissedThenNow(true)}
            className="absolute top-4 right-4 text-[#A8A29E] hover:text-[#F5F1E7] transition-colors duration-300"
          >
            <X size={18} />
          </button>
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
            <History size={14} /> Then vs. Now · {relativeTime(thenNow.created_at)}
          </p>
          <p className="text-[#A8A29E] text-sm mb-3">
            On {longDate(thenNow.created_at)} you wrote:
          </p>
          {thenNow.prompt && (
            <p className="italic-serif text-[#D4AF37]/80 text-sm mb-2">{thenNow.prompt}</p>
          )}
          <blockquote className="text-[#F5F1E7] italic-serif text-xl leading-relaxed line-clamp-4 whitespace-pre-wrap">
            “{thenNow.content}”
          </blockquote>
          <p className="mt-4 text-[#A8A29E] leading-relaxed">
            Read it as who you are today. What has shifted? What is still true?
          </p>
          <button
            data-testid="then-vs-now-reflect"
            onClick={reflectOnPast}
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] px-6 py-2.5 text-sm hover:bg-[#D4AF37]/10 transition-colors duration-300"
          >
            Reflect on this now
          </button>
        </div>
      )}

      {/* Continuous timeline of past reflections */}
      <div className="mt-16">
        <h3 className="serif text-2xl text-[#F5F1E7] mb-8">Your unfolding story</h3>
        {entries.length === 0 ? (
          <p className="text-[#A8A29E] italic-serif text-lg">Nothing kept yet. Begin above.</p>
        ) : (
          <div data-testid="journal-entries" className="relative border-l border-white/10 pl-8 space-y-10">
            {entries.map((e) => (
              <div key={e.id} data-testid="journal-entry" className="group relative">
                <span className="absolute -left-[41px] top-1.5 h-3 w-3 rounded-full bg-[#D4AF37]/70 ring-4 ring-[#110D14]" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#A8A29E]">
                    {longDate(e.created_at)}
                    {e.mood ? ` · ${e.mood}` : ""} · {relativeTime(e.created_at)}
                  </span>
                  <button
                    data-testid={`delete-entry-${e.id}`}
                    onClick={() => remove(e.id)}
                    className="text-[#A8A29E] hover:text-[#C1272D] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                {e.prompt && <p className="italic-serif text-[#D4AF37]/80 mt-2 text-sm">{e.prompt}</p>}
                <p className="mt-2 text-[#F5F1E7] whitespace-pre-wrap leading-relaxed text-lg">{e.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      </>
      )}
    </main>
  );
}
